// Deterministic-ish dummy ASO audit generator from a search query (app name or URL).
// Same input → same output. Used by the Free ASO Audit page.

const APP_NAMES = {
  spotify: "Spotify - Music and Podcasts",
  instagram: "Instagram",
  whatsapp: "WhatsApp Messenger",
  tinder: "Tinder Dating App",
  duolingo: "Duolingo: Language Lessons",
  notion: "Notion - notes, docs, tasks",
  uber: "Uber - Request a ride",
  swiggy: "Swiggy Food & Grocery Order",
  zomato: "Zomato Online Food Delivery",
  paytm: "Paytm: Secure UPI Payments",
};

function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function pseudoRandom(seed, min, max, decimals = 0) {
  const v = (seed % 10000) / 10000;
  const n = min + v * (max - min);
  return Number(n.toFixed(decimals));
}

function detectPlatform(query) {
  const q = query.toLowerCase();
  if (q.includes("apps.apple.com") || q.includes("itunes.apple.com")) return "ios";
  if (q.includes("play.google.com")) return "android";
  return "both";
}

function extractName(query) {
  const cleaned = query.trim().toLowerCase();
  for (const k of Object.keys(APP_NAMES)) {
    if (cleaned.includes(k)) return APP_NAMES[k];
  }
  // Try to extract from app store URL
  const m = query.match(/\/([a-z0-9\-]+)\/(id\d+|[a-z0-9.]+)/i);
  if (m) {
    const slug = m[1].replace(/-/g, " ");
    return slug.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return query.length > 40 ? "Your App" : query.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function generateAudit(query) {
  const seed = hashString(query.trim().toLowerCase() || "default");
  const appName = extractName(query);
  const platform = detectPlatform(query);

  const overall = pseudoRandom(seed * 31, 42, 71);
  const rating = pseudoRandom(seed * 17, 3.4, 4.4, 1);
  const reviews = Math.round(pseudoRandom(seed * 11, 800, 24000));
  const installs = `${Math.round(pseudoRandom(seed * 13, 50, 980))}K+`;
  const cvr = pseudoRandom(seed * 7, 14, 28, 1);

  const scores = {
    title: pseudoRandom(seed * 3, 38, 72),
    subtitle: pseudoRandom(seed * 5, 30, 68),
    keywords: pseudoRandom(seed * 19, 28, 64),
    description: pseudoRandom(seed * 23, 44, 78),
    screenshots: pseudoRandom(seed * 29, 36, 70),
    icon: pseudoRandom(seed * 37, 55, 88),
    ratings: pseudoRandom(seed * 41, 40, 76),
    localization: pseudoRandom(seed * 43, 18, 56),
  };

  const keywords = [
    {
      keyword: "music streaming",
      rank: Math.round(pseudoRandom(seed * 51, 11, 78)),
      volume: "High",
      difficulty: "8.4",
    },
    {
      keyword: `${appName.toLowerCase().split(" ")[0]} app`,
      rank: Math.round(pseudoRandom(seed * 53, 4, 22)),
      volume: "High",
      difficulty: "6.1",
    },
    {
      keyword: "best music app",
      rank: Math.round(pseudoRandom(seed * 57, 28, 110)),
      volume: "Medium",
      difficulty: "7.2",
    },
    {
      keyword: "free songs offline",
      rank: Math.round(pseudoRandom(seed * 59, 14, 64)),
      volume: "Medium",
      difficulty: "5.5",
    },
    {
      keyword: "podcast app",
      rank: Math.round(pseudoRandom(seed * 61, 18, 90)),
      volume: "Medium",
      difficulty: "6.8",
    },
  ];

  const gaps = [
    {
      title: "Title is missing 2 high-volume keywords",
      severity: "high",
      detail:
        "Your app title currently uses 24 / 30 characters. Adding 'streaming' and your top category modifier could lift impressions ~28%.",
    },
    {
      title: "Subtitle conversion-rate optimization missing",
      severity: "high",
      detail:
        "Subtitle is generic. A benefit-led subtitle (e.g. 'Stream millions of songs offline') typically lifts CVR 12–18% on iOS.",
    },
    {
      title: "Screenshot 1 lacks a value proposition",
      severity: "medium",
      detail:
        "First screenshot is a UI shot with no headline. Top-converting apps in your category use a short benefit headline + product visual.",
    },
    {
      title: "App preview video not present",
      severity: "medium",
      detail:
        "Apps with a preview video convert 25–35% better on iOS. Recommend a 15–20s screen-recording cut.",
    },
    {
      title: "Localization gap — only English shipped",
      severity: "medium",
      detail:
        "Top-5 markets you could expand to: USA, UK, India, Brazil, Germany. Localized listings typically lift installs ~40%.",
    },
    {
      title: "Ratings prompt logic is missing",
      severity: "low",
      detail:
        "We see no smart prompt at first-win moment. Implementing one would lift your rating 0.3–0.6 stars over 60 days.",
    },
    {
      title: "Reply rate to 1–3★ reviews under 18%",
      severity: "low",
      detail:
        "Best-in-class apps reply to 100% of low-star reviews within 24h. This is a free conversion lever.",
    },
  ];

  const competitors = [
    { name: "Top competitor A", rating: 4.7, score: 82 },
    { name: "Top competitor B", rating: 4.5, score: 76 },
    { name: appName, rating, score: overall },
    { name: "Top competitor C", rating: 4.3, score: 71 },
  ].sort((a, b) => b.score - a.score);

  return {
    appName,
    platform,
    overall,
    rating,
    reviews,
    installs,
    cvr,
    scores,
    keywords,
    gaps,
    competitors,
  };
}
